// auth.js  
import React, { useEffect, useState } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { jwtDecode } from 'jwt-decode';
import { UserManager } from 'oidc-client-ts';
import oidcConfig from './aws-config';

const userManager = new UserManager(oidcConfig);
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

// ---------- utils ----------
async function parseJsonSafe(resp) {
  const ct = resp.headers.get('content-type') || '';
  const txt = await resp.text();
  if (ct.includes('application/json') && txt) { try { return JSON.parse(txt); } catch {} }
  return txt || null;
}

// --- decode helpers ---
function decodeJwtSafe(t) {
  try {
    const [, p] = (t || '').split('.');
    return JSON.parse(atob((p || '').replace(/-/g, '+').replace(/_/g, '/'))) || {};
  } catch { return {}; }
}
function expOf(t) {
  return Number(decodeJwtSafe(t).exp || 0);
}

/**
 * Best-effort read of which SSO provider the current user signed in with, from
 * Cognito's standard `identities` federated-IdP claim on the ID token. Returns
 * 'google' | 'microsoft' | null (null if there's no federated identity claim,
 * e.g. a native Cognito user/password account, or the claim shape doesn't
 * match what's expected here -- callers should treat null as "unknown, don't
 * block" rather than assuming a specific provider).
 */
export function getSignInProvider(idToken) {
  const token = idToken || localStorage.getItem('id_token');
  const claims = decodeJwtSafe(token);
  const raw = claims?.identities;
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const providerName = String(parsed?.[0]?.providerName || '').toLowerCase();
    if (providerName.includes('google')) return 'google';
    if (providerName.includes('microsoft') || providerName.includes('azure') || providerName.includes('entra')) return 'microsoft';
    return null;
  } catch {
    return null;
  }
}
function idTokenTTLSeconds(t) {
  return expOf(t) - Math.floor(Date.now() / 1000);
}
function newer(a, b) {
  return (expOf(a) || 0) > (expOf(b) || 0);
}

// --- storage writes ---
function saveTokenIfNewer(key, val, _label = key) {
  if (!val) return;
  const old = localStorage.getItem(key);
  if (!old || newer(val, old)) {
    localStorage.setItem(key, val);
    window.dispatchEvent(new Event('auth-tokens-changed'));
  }
}
function readIdToken() { return localStorage.getItem('id_token'); }
function readRefreshToken() { return localStorage.getItem('refresh_token'); }

// --- keep OIDC userStore in sync with localStorage ---
async function syncOidcFromLocal() {
  try {
    const user = await userManager.getUser();
    if (!user) return;
    const idt = readIdToken();
    if (idt) {
      user.id_token = idt;
      user.expires_at = expOf(idt);
      await userManager.storeUser(user);
    }
  } catch {}
}

// --- Single-flight refresh guard to avoid races ---
let refreshing = null;
async function refreshOnce() {
  if (refreshing) return refreshing;
  const rt = readRefreshToken();
  if (!rt) return;

  refreshing = (async () => {
    const resp = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
      credentials: 'omit'
    });
    const data = await parseJsonSafe(resp);

    if (!resp.ok) {
      console.error('[auth] refresh failed', resp.status, data);

      ['id_token','access_token','refresh_token'].forEach(k => localStorage.removeItem(k));
      window.dispatchEvent(new Event('auth-tokens-changed'));

      try {
        await userManager.removeUser();   // <- clear OIDC stored user too
      } catch (e) {
        console.warn('[auth] failed to remove OIDC user', e);
      }

      if (typeof window !== 'undefined') {
        window.location.replace('/');
      }

      throw new Error(`[auth] refresh failed: ${resp.status} ${JSON.stringify(data)}`);
    }

    if (data?.id_token)     saveTokenIfNewer('id_token', data.id_token, 'id_token');
    if (data?.access_token) saveTokenIfNewer('access_token', data.access_token, 'access_token');
    if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);

    await syncOidcFromLocal();
  })().finally(() => { refreshing = null; });

  return refreshing;
}

export { refreshOnce };

// --- TTL guard + focus/visibility wake-up ---
function installIdTokenGuard() {
  const THRESHOLD = 300; // refresh if < 5 minutes left
  let timer;

  const check = async () => {
    try {
      const idt = readIdToken();
      const left = idTokenTTLSeconds(idt);

      if (!idt) {
        // no token → nothing to refresh
        return;
      }

      if (left < THRESHOLD) {
        await refreshOnce();
      }
    } catch {
      // ignore; next tick will retry
    } finally {
      schedule();
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    const idt = readIdToken();
    const left = idTokenTTLSeconds(idt);
    // check again in 5–10 minutes, or a bit before expiry if sooner
    const nextMs = Math.max(5 * 60_000, Math.min((left - 240) * 1000, 10 * 60_000));
    timer = setTimeout(check, Number.isFinite(nextMs) ? nextMs : 5 * 60_000);
  };

  const onWake = () => check();
  const onVis = () => { if (!document.hidden) check(); };
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onVis);
  check(); // start immediately

  return () => {
    clearTimeout(timer);
    window.removeEventListener('focus', onWake);
    document.removeEventListener('visibilitychange', onVis);
  };
}

// expose tiny console API 
if (typeof window !== 'undefined') {
  window.__authDebug = {
    show() {
      const t = readIdToken();
      if (!t) { console.log('[authDebug.show] no id_token'); return; }
      const { exp, sub, email } = decodeJwtSafe(t);
      const ttl = idTokenTTLSeconds(t);
      console.log('[authDebug.show]', { exp, sub, email, ttl_s: ttl });
    },
    simulateExpiry() {
      const past = Math.floor(Date.now()/1000) - 60;
      const payload = btoa(JSON.stringify({ exp: past, sub: 'debug' }))
        .replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
      const fake = `x.${payload}.y`;
      localStorage.setItem('id_token', fake);
      window.dispatchEvent(new Event('auth-tokens-changed'));
      console.warn('[authDebug.simulateExpiry] wrote expired id_token');
    },
    async forceRefresh() {
      const rt = readRefreshToken();
      if (!rt) return console.warn('no refresh_token in storage');
      return refreshOnce();
    },
    async forceRefreshAndSave() {
      await refreshOnce();
      await syncOidcFromLocal();
      window.__authDebug.show();
    },
    clear() {
      ['id_token','access_token','refresh_token'].forEach(k => localStorage.removeItem(k));
      window.dispatchEvent(new Event('auth-tokens-changed'));
    }
  };
}

// ---------- hook ----------
export const useAuth = () => {
  const auth = useOidcAuth();

  // React to token writes & cross-tab changes
  const [localVersion, setLocalVersion] = useState(0);
  useEffect(() => {
    const bump = () => setLocalVersion(v => v + 1);
    window.addEventListener('auth-tokens-changed', bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener('auth-tokens-changed', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  // Auto-install the TTL guard once 
  useEffect(() => {
    const uninstall = installIdTokenGuard();
    return () => uninstall && uninstall();
  }, []);

  // ----- Local session detection -----
  void localVersion;
  function isTokenStillValid(t) {
    if (!t) return false;
    return idTokenTTLSeconds(t) > 0; // > 0 seconds left
  }

  const localIdToken =
    typeof window !== 'undefined' ? localStorage.getItem('id_token') : null;
  const localAccessToken =
    typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;

  // only count as "local authed" if token exists AND not expired
  const isLocalAuthed = !!(
    localIdToken && isTokenStillValid(localIdToken)
  );

  let localProfile = null;
  if (localIdToken) {
    try { localProfile = jwtDecode(localIdToken); } catch { localProfile = null; }
  }

  const isAuthenticated = isLocalAuthed;

  const mergedUser = auth.user
    ? {
        ...auth.user,
        profile: auth.user.profile,
        access_token: auth.user.access_token || auth.user.accessToken,
        sub: auth.user.profile?.sub || auth.user.sub,
      }
    : localProfile
    ? {
        profile: localProfile,
        email: localProfile.email,
        sub: localProfile.sub,
        access_token: localStorage.getItem('access_token'),
        id_token: localIdToken,
      }
    : null;

  const isLoading = auth.isLoading && !isLocalAuthed;

  const signOut = async () => {
    try {
      ['access_token','id_token','refresh_token'].forEach(k => localStorage.removeItem(k));
      window.dispatchEvent(new Event('auth-tokens-changed'));
    } catch {}
    if (auth.isAuthenticated && auth.signoutRedirect) {
      try { await auth.signoutRedirect(); return; } catch (e) { console.error(e); }
    }
    if (typeof window !== 'undefined') window.location.replace('/');
  };

  // ---------- refreshed + instrumented ----------
  const refreshTokens = async () => {
    try { await refreshOnce(); } catch (e) { console.error('[auth] refreshTokens():', e); }
  };

  // persist newest tokens coming from oidc lib 
  useEffect(() => {
    const u = auth?.user;
    if (!u) return;
    if (u?.access_token) saveTokenIfNewer('access_token', u.access_token, 'lib.access_token');
    if (u?.id_token)     saveTokenIfNewer('id_token', u.id_token, 'lib.id_token');
    if (u?.refresh_token) localStorage.setItem('refresh_token', u.refresh_token);
  }, [auth?.user]);

  return {
    isAuthenticated,
    isLoading,
    error: auth.error,
    user: mergedUser,
    signOut,
    signinRedirect: auth.signinRedirect,
    refreshTokens, 
  };
};
